import { api } from ".";

/** Normalized payer id (+ optional display name from API). */
export interface IContractModelingPayerRef {
  id: number;
  name: string;
}

function normalizePayersWithDataResponse(
  response: unknown
): IContractModelingPayerRef[] {
  if (response == null) return [];

  let raw: unknown = response;
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    raw = (raw as { data: unknown }).data;
  }

  if (!Array.isArray(raw) || raw.length === 0) return [];

  const first = raw[0];
  if (typeof first === "number") {
    return (raw as number[]).map((id) => ({ id, name: "" }));
  }
  if (typeof first === "string") {
    return (raw as string[])
      .map((s) => {
        const id = parseInt(s, 10);
        return Number.isNaN(id) ? null : { id, name: "" };
      })
      .filter((p): p is IContractModelingPayerRef => p !== null);
  }

  return (raw as unknown[])
    .map((row): IContractModelingPayerRef | null => {
      if (row == null || typeof row !== "object") return null;
      const r = row as Record<string, unknown>;
      const idRaw = r.payerId ?? r.id ?? r.payer_id;
      if (idRaw == null) return null;
      const id =
        typeof idRaw === "string" ? parseInt(idRaw, 10) : Number(idRaw);
      if (!Number.isFinite(id)) return null;
      const nameRaw = r.payerName ?? r.name ?? r.payer_name ?? "";
      const name = typeof nameRaw === "string" ? nameRaw : "";
      return { id, name };
    })
    .filter((p): p is IContractModelingPayerRef => p !== null);
}

// --- Payer comparison: POST /contract-modeling/analysis ---

export interface IContractModelingAnalysisRequest {
  primaryPayerId: number;
  comparisonPayerIds: number[];
  /** ISO date (YYYY-MM-DD) — contract fee schedule effective date for all payers. */
  feeEffectiveDate: string;
  /** Provider id; backend scopes modeling data to this provider. */
  providerId: string;
  cptCodes?: string[];
  startDate?: string;
  endDate?: string;
}

export interface IContractModelingPrimaryCell {
  feeAmount: number;
  revenue: number;
}

export interface IContractModelingComparisonCell {
  payerId: number;
  payerName: string;
  feeAmount: number;
  revenue: number;
  feeVsPrimaryRatio: number;
}

export interface IContractModelingRow {
  cptCode: string;
  volume: number;
  primaryPayer: IContractModelingPrimaryCell;
  comparisonPayers: IContractModelingComparisonCell[];
  revenuePotential: number;
  revenuePotentialPercent: number | null;
}

export interface IContractModelingComparisonTotal {
  payerId: number;
  payerName: string;
  totalRevenue: number;
}

export interface IContractModelingAnalysisResponse {
  feeEffectiveDate: string;
  primaryPayer: { id: number; name: string };
  comparisonPayers: { id: number; name: string }[];
  rows: IContractModelingRow[];
  totals: {
    primaryPayerRevenue: number;
    comparisonPayerRevenues: IContractModelingComparisonTotal[];
    revenuePotential: {
      current: number;
      potential: number;
      difference: number;
      revenuePotentialPercent: number | null;
    };
  };
}

// --- Term comparison: POST /contract-modeling/year-comparison ---

export interface ITermComparisonRequest {
  payerId: number;
  primaryEffectiveDate: string;
  comparisonEffectiveDates: string[];
  /** Provider id; backend scopes modeling data to this provider. */
  providerId: string;
  cptCodes?: string[];
  /**
   * Exact-string, case-sensitive — must match values from
   * GET /procedures/service-lines. Intersected with `cptCodes` when both are
   * sent (empty intersection ⇒ 200 with rows: []).
   */
  serviceLines?: string[];
  startDate?: string;
  endDate?: string;
}

export interface ITermComparisonColumn {
  effectiveDate: string;
  feeAmount: number;
  revenue: number;
  feeVsPrimaryRatio: number;
}

export interface ITermComparisonRow {
  cptCode: string;
  /** Optional service-line label for the CPT; null/undefined until backfill completes. */
  serviceLine?: string | null;
  volume: number;
  primaryTerm: { feeAmount: number; revenue: number };
  comparisonTerms: ITermComparisonColumn[];
  revenuePotential: number;
  revenuePotentialPercent: number | null;
}

export interface ITermComparisonResponse {
  payer: { id: number; name: string };
  primaryEffectiveDate: string;
  comparisonEffectiveDates: string[];
  claimWindow: { startDate: string | null; endDate: string | null };
  rows: ITermComparisonRow[];
  totals: {
    primaryRevenue: number;
    comparisonRevenues: { effectiveDate: string; totalRevenue: number }[];
    revenuePotential: {
      current: number;
      potential: number;
      difference: number;
      revenuePotentialPercent: number | null;
    };
  };
}

function normalizeFeeTermDatesResponse(response: unknown): string[] {
  if (response == null) return [];
  let raw: unknown = response;
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    raw = (raw as { data: unknown }).data;
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x));
}

const contractModelingQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getContractModelingPayersWithData: builder.query<
      IContractModelingPayerRef[],
      { providerId: string }
    >({
      query: ({ providerId }) => ({
        url: "/contract-modeling/payers-with-data",
        params: { providerId },
      }),
      transformResponse: (response: unknown) =>
        normalizePayersWithDataResponse(response),
      providesTags: ["ContractInsights"],
    }),
    getContractModelingAvailableCptCodes: builder.query<
      string[],
      {
        payerId?: number;
        startDate?: string;
        endDate?: string;
        providerId?: string;
      } | void
    >({
      query: (params) => ({
        url: "/contract-modeling/available-cpt-codes",
        params: params || {},
      }),
      transformResponse: (response: { data: string[] }) => response.data,
      providesTags: ["ContractInsights"],
    }),
    getContractModelingAvailableFilters: builder.query<
      { cptCodes: string[]; serviceLines: string[] },
      {
        payerId?: number;
        startDate?: string;
        endDate?: string;
        providerId?: string;
      } | void
    >({
      query: (params) => ({
        url: "/contract-modeling/available-filters",
        params: params || {},
      }),
      transformResponse: (response: {
        data: { cptCodes: string[]; serviceLines: string[] };
      }) => response.data ?? { cptCodes: [], serviceLines: [] },
      providesTags: ["ContractInsights"],
    }),
    getContractModelingAvailableFeeTermDates: builder.query<
      string[],
      { payerId: number; providerId: string }
    >({
      query: ({ payerId, providerId }) => ({
        url: "/contract-modeling/available-years",
        params: { payerId, providerId },
      }),
      transformResponse: (response: unknown) =>
        normalizeFeeTermDatesResponse(response),
      providesTags: ["ContractInsights"],
    }),
    postContractModelingAnalysis: builder.mutation<
      IContractModelingAnalysisResponse,
      IContractModelingAnalysisRequest
    >({
      query: (body) => ({
        url: "/contract-modeling/analysis",
        method: "POST",
        body,
      }),
      transformResponse: (response: {
        data: IContractModelingAnalysisResponse;
      }) => response.data,
    }),
    postContractModelingTermComparison: builder.mutation<
      ITermComparisonResponse,
      ITermComparisonRequest
    >({
      query: (body) => ({
        url: "/contract-modeling/year-comparison",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: ITermComparisonResponse }) =>
        response.data,
    }),
  }),
});

export const {
  useGetContractModelingPayersWithDataQuery,
  useGetContractModelingAvailableCptCodesQuery,
  useGetContractModelingAvailableFiltersQuery,
  useGetContractModelingAvailableFeeTermDatesQuery,
  usePostContractModelingAnalysisMutation,
  usePostContractModelingTermComparisonMutation,
} = contractModelingQueries;
